import React, { useState, useEffect } from "react"

import Typography from "@material-ui/core/Typography"
import Container from "@material-ui/core/Container"
import Divider from "@material-ui/core/Divider"
import Button from "@material-ui/core/Button"
import CircularProgress from "@material-ui/core/CircularProgress"
import Grid from "@material-ui/core/Grid"

import PhotoLibraryOutlinedIcon from "@material-ui/icons/PhotoLibraryOutlined"
import SaveAltIcon from "@material-ui/icons/SaveAlt"

import { makeStyles } from "@material-ui/core"

import useDefaultStyles from "../../../styles/default"

import api from "../../../services/api"
import { useNavigation } from "../../../services/navigation"

import { useHomeState } from "../../../store/home"

import { toBytes } from "../../../utils/sanitization"

import androidIcon from "../../../assets/android_icon.png"
import iosIcon from "../../../assets/ios_icon.png"

import Notification from "../../../components/Notification"

const useStyles = makeStyles(() => ({
	orangeButton: {
		backgroundColor: "#FAAD14",
		borderColor: "#FAAD14",
		"&:hover": {
      backgroundColor: "#FAAD14",
      borderColor: "#FAAD14",
    },
    "&:active": {
      backgroundColor: "#FAAD14",
      borderColor: "#FAAD14",
		},
		color: "#FFF",
		width: "100%"
	},
	centeredContent: {
		textAlign: "center"
	},
	small: {
		fontSize: "12px"
	}
}))

const Download = () => {
	const homeState = useHomeState()

	const [loadingZip, setLoadingZip] = useState(false)
	const [isZipReady, setIsZipReady] = useState(true)
	const [loadingRequest, setLoadingRequest] = useState(true)

	const [zipLink, setZipLink] = useState("")
	const [zipSize, setZipSize] = useState("")
	const [zipFilesCount, setZipFilesCount] = useState("")

	const { getRouteParams, navigate } = useNavigation()

	const { hash } = getRouteParams()

	const defaultClasses = useDefaultStyles()
	const classes = useStyles()

	const handleDownloadZip = async () => {
		Notification.success("Em alguns instantes suas fotos serão baixadas")

		// Generates a zip download log
		api.post(`/gallery/${hash}/zip/log`).catch(() => {})

		// Generates a gallery activity log
		api.post(`/gallery/${homeState.gallery.collection_id}/activity`, {
			activity_type: "downloaded_zip"
		}).catch(() => {})
	}

	const handleCheckZip = async () => {
		const response = await api.get(`/gallery/${hash}/zip`)

		const { zip_status, zip_url, zip_size, zip_files_count } = response.data

		return {
			ready: zip_status === "complete",
			url: zip_url,
			size: zip_size,
			files_count: zip_files_count
		}
	}

	const handleRequestZip = async () => {
		setLoadingZip(true)

		await api.get(`/gallery/${hash}/zip?generate=true`)

		checkZip: while (!isZipReady) {
			const data = await handleCheckZip()

			if (data.ready) {
				setLoadingZip(false)

				setZipData(data)

				Notification.success("Arquivo .ZIP gerado com sucesso!")

				break checkZip
			}
		}
	}

	const setZipData = ({ size, files_count, url, ready }) => {
		setZipSize(size)
		setZipFilesCount(files_count)
		setZipLink(url)
		setIsZipReady(ready)
	}

	const getData = async () => {
		// In case user is not allowed to download pictures we redirect him back
		// to the initial page
		if (!homeState.front.download_all) {
			return navigate(`/${hash}`)
		}

		const data = await handleCheckZip()

		setZipData(data)

		setLoadingRequest(false)
	}

	useEffect(() => {
		getData()
	}, [])

	if (loadingRequest) {
		return (
			<Grid container justify="center" alignContent="space-around" style={{ height: "100%" }}>
				<CircularProgress />
			</Grid>
		)
	} else {
		return (
			<Container className={defaultClasses.container}>
				{isZipReady ? (
				<>
					<Typography variant="h6">
						{homeState.gallery.title}
					</Typography>

					<Typography variant="subtitle1">
						Faça o download de todas as fotos
					</Typography>
					
					<Divider className={defaultClasses.invisibleDividerLarge} />

					<Grid container spacing={3} justify="center">
						<Grid item>
							<Container className={classes.centeredContent}>
								<PhotoLibraryOutlinedIcon fontSize="small" />
								<Typography variant="subtitle1" style={{ fontSize: 14 }}>
									{zipFilesCount || homeState.gallery.info.total_pictures}{homeState.gallery.info.total_pictures === 1 ? " FOTO" : " FOTOS"}
								</Typography>
							</Container>
						</Grid>
						
						<Grid item>
							<Divider orientation="vertical" light />
						</Grid>
						
						<Grid item>
							<Container className={classes.centeredContent}>
								<SaveAltIcon fontSize="small" />
								<Typography variant="subtitle1" style={{ fontSize: 14 }}>
									{toBytes(zipSize || homeState.gallery.info.total_size)}
								</Typography>
							</Container>
						</Grid>
					</Grid>

					<Divider className={defaultClasses.invisibleDividerLarge} />

					<Button
						variant="contained"
						color="secondary"
						className={defaultClasses.buttonBlock}
						style={{ color: "#FFF" }}
						href={zipLink}
						onClick={handleDownloadZip}
					>
						DOWNLOAD (.ZIP)
					</Button>
					<Typography variant="subtitle1" className={classes.small}>
						* No arquivo .zip tem todas as suas fotos.
					</Typography>

					<Divider className={defaultClasses.invisibleDividerLarge} />

					<Grid container spacing={2} wrap="nowrap">
						<Grid item>
							<img src={androidIcon} />
						</Grid>
						<Grid item>
							<Typography variant="subtitle1" className={classes.small}>
								PARA ABRIR O .ZIP NO <strong>ANDROID</strong>
								<br />
								Se o .zip não abrir em seu ANDROID, baixe o app “RAR” na Play Store para descompactar as suas fotos.
							</Typography>
						</Grid>
					</Grid>

					<Divider className={defaultClasses.invisibleDividerLarge} />

					<Grid container spacing={2} wrap="nowrap">
						<Grid item>
							<img src={iosIcon} />
						</Grid>
						<Grid item>
							<Typography variant="subtitle1" className={classes.small}>
								PARA ABRIR O .ZIP NO <strong>IPHONE</strong>
								<br />
								Se o .zip não abrir em seu IPHONE, baixe o app “WinZip” na App Store para descompactar as suas fotos.
							</Typography>
						</Grid>
					</Grid>
				</>
			): (
				<>
					<Typography variant="h6">
						Download de todas as fotos da galeria
					</Typography>

					<Divider className={defaultClasses.invisibleDividerSmall} />

					<Typography variant="subtitle1">
						Para fazer o download é necessário gerar um arquivo .zip com todas as fotos.
					</Typography>

					<Divider className={defaultClasses.invisibleDividerMedium} />

					<Button
						variant="contained"
						className={classes.orangeButton}
						onClick={() => handleRequestZip()}
						disabled={loadingZip}
						endIcon={loadingZip && <CircularProgress size={20} />}
					>
						{loadingZip ? "GERANDO ARQUIVO .ZIP" : "GERAR ARQUIVO .ZIP"}
					</Button>
				</>
			)}
			</Container>
		)
	}
}

export default Download
